import { useCallback, useEffect, useState } from 'react';
import { databaseBackupsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { isValidRestoreConfirm } from '../domain/system/databaseBackupCopy';

async function messageFromError(err, fallback) {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message;
    } catch {
      /* not JSON */
    }
  }
  return getApiErrorMessage(err, fallback);
}

export default function useDatabaseBackups({ enabled } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError('');
    try {
      const res = await databaseBackupsAPI.list();
      setData(res.data?.data || null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load backups'));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const snapshot = useCallback(
    async (note) => {
      setBusy('snapshot');
      setError('');
      setSuccess('');
      try {
        await databaseBackupsAPI.snapshot({ note });
        setSuccess('Current database saved as a restore point.');
        await load();
      } catch (err) {
        setError(getApiErrorMessage(err, 'Could not snapshot the database'));
      } finally {
        setBusy('');
      }
    },
    [load]
  );

  const upload = useCallback(
    async (file, note) => {
      if (!file) {
        setError('Choose a .dump or .sql file first');
        return;
      }
      setBusy('upload');
      setError('');
      setSuccess('');
      try {
        const body = new FormData();
        body.append('file', file);
        if (note) body.append('note', note);
        await databaseBackupsAPI.upload(body);
        setSuccess('Backup uploaded. Oldest restore point is dropped when more than five exist.');
        await load();
      } catch (err) {
        setError(getApiErrorMessage(err, 'Could not upload the backup'));
      } finally {
        setBusy('');
      }
    },
    [load]
  );

  const restore = useCallback(
    async (id, confirm) => {
      if (!isValidRestoreConfirm(confirm)) {
        setError('Type RESTORE to confirm a full rollback');
        return false;
      }
      setBusy(`restore:${id}`);
      setError('');
      setSuccess('');
      try {
        await databaseBackupsAPI.restore(id, { confirm });
        setSuccess(
          'Database restored. A safety snapshot of the previous data was kept. Sign in again if your session ended.'
        );
        await load();
        return true;
      } catch (err) {
        setError(await messageFromError(err, 'Could not restore the database'));
        return false;
      } finally {
        setBusy('');
      }
    },
    [load]
  );

  const download = useCallback(async (id, filename) => {
    setBusy(`download:${id}`);
    setError('');
    try {
      const res = await databaseBackupsAPI.download(id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'sms-backup.dump';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(await messageFromError(err, 'Could not download the backup'));
    } finally {
      setBusy('');
    }
  }, []);

  const remove = useCallback(
    async (id) => {
      setBusy(`delete:${id}`);
      setError('');
      setSuccess('');
      try {
        await databaseBackupsAPI.remove(id);
        setSuccess('Restore point removed.');
        await load();
      } catch (err) {
        setError(getApiErrorMessage(err, 'Could not delete the backup'));
      } finally {
        setBusy('');
      }
    },
    [load]
  );

  return {
    data,
    loading,
    busy,
    error,
    success,
    load,
    snapshot,
    upload,
    restore,
    remove,
    download,
  };
}
